import * as React from 'react';

/**
 * AuctiSync brand logo — finalized brand system (2026-07-08).
 *
 * Replaces the provisional "soundwave" mark. Two exports:
 *   - <BrandMark />  the interlocking "sync" icon on its own.
 *   - <Logo />       the full horizontal lockup (icon + "AuctiSync" wordmark).
 *
 * The wordmark is delivered as VECTORS (Rethink Sans letterforms outlined by the
 * brand studio) so the exact brand type renders everywhere without loading the
 * font. By default the wordmark uses `currentColor`, so it inherits the
 * surrounding text colour and adapts to light/dark automatically; the two-tone
 * blue icon stays fixed (reads on both white and near-black). Pass `tone` to
 * force a monochrome treatment for solid-colour or photographic backgrounds.
 *
 * Palette (brand style guide):
 *   light  #5A8CFF   ·   action #2264DC   ·   ink/navy #0A0045
 */

// ─── Brand palette ────────────────────────────────────────────────────────
const BRAND_LIGHT = '#5A8CFF';
const BRAND_MID = '#2264DC';
const BRAND_NAVY = '#0A0045';

export type BrandTone = 'color' | 'mono-white' | 'mono-navy';

// ─── Icon geometry (standalone mark) — viewBox 132.1 × 176.83 ─────────────
const ICON_MARK_LIGHT = [
  '23.29 101.08 58.48 143.56 42.76 176.83 7.54 134.43 23.29 101.08',
  '108.82 75.75 85.39 47.49 73.63 33.27 89.34 0 124.56 42.4 108.82 75.75',
];
const ICON_MARK_MID = [
  '85.39 47.49 69.7 80.7 32.9 80.72 23.28 101.08 0 72.96 12.03 47.49 85.39 47.49',
  '46.72 129.34 62.41 96.13 99.2 96.11 108.82 75.75 132.1 103.87 120.07 129.34 46.72 129.34',
];

// ─── Lockup geometry — viewBox 359.2 × 89.32 ──────────────────────────────
// Icon polygons (positioned within the lockup).
const LOCKUP_ICON_LIGHT = [
  '11.76 51.05 29.54 72.51 21.6 89.32 3.81 67.9 11.76 51.05',
  '54.96 38.26 43.13 23.99 37.19 16.81 45.13 0 62.92 21.42 54.96 38.26',
];
const LOCKUP_ICON_MID = [
  '43.13 23.99 35.2 40.76 16.62 40.77 11.76 51.05 0 36.85 6.08 23.99 43.13 23.99',
  '23.6 65.33 31.52 48.55 50.11 48.54 54.96 38.26 66.72 52.46 60.65 65.33 23.6 65.33',
];
// "AuctiSync" wordmark — outlined Rethink Sans. `paths` + `polys` + one rect.
const WORDMARK_PATHS = [
  'M258.71,45.61c-1.17-.94-2.4-1.68-3.68-2.25-1.28-.56-2.43-1.02-3.47-1.37-1.85-.68-3.52-1.33-5.04-1.95-1.51-.62-2.71-1.34-3.6-2.17-.87-.84-1.32-1.87-1.32-3.06,0-1.63.55-2.86,1.65-3.7,1.1-.83,2.44-1.25,4-1.25,1.98,0,3.51.56,4.64,1.68,1.11,1.11,1.7,2.41,1.74,3.93h8.08c-.04-2.63-.72-4.84-2-6.63-1.31-1.8-3.01-3.15-5.12-4.06-2.1-.9-4.45-1.37-7.04-1.37s-4.89.45-6.98,1.34c-2.11.89-3.79,2.24-5.03,4-1.22,1.77-1.85,3.97-1.85,6.6,0,1.76.37,3.38,1.09,4.87.73,1.48,1.92,2.82,3.57,4.03,1.66,1.19,3.83,2.25,6.55,3.16,2.09.69,3.77,1.28,5.05,1.83,1.28.53,2.27,1.07,2.98,1.63.71.57,1.18,1.15,1.43,1.8.27.64.39,1.36.39,2.21,0,1.63-.59,2.93-1.76,3.88-1.18.95-2.78,1.43-4.78,1.43-2.33,0-4.14-.66-5.45-1.98-1.33-1.3-2.03-2.94-2.1-4.9h-7.97c.08,2.95.8,5.41,2.19,7.38,1.38,1.98,3.24,3.46,5.57,4.45,2.34.99,4.95,1.49,7.81,1.49s5.23-.5,7.41-1.49c2.18-.99,3.9-2.41,5.18-4.24,1.28-1.84,1.93-4.02,1.93-6.57,0-2.08-.39-3.81-1.15-5.23-.77-1.41-1.74-2.58-2.92-3.52Z',
  'M315.14,35.61c-1.73,0-3.45.35-5.17,1.04-1.71.7-3.07,1.74-4.09,3.14l-1.25-3.47h-6.24v29.62h7.49v-16.42c0-2.24.59-4.03,1.74-5.39,1.17-1.38,2.83-2.07,4.99-2.07s3.81.69,4.93,2.07c1.11,1.36,1.69,3.16,1.69,5.39v16.42h7.47v-17.44c0-4.18-1.07-7.37-3.23-9.59-2.18-2.21-4.94-3.31-8.33-3.31Z',
  'M348.94,59.09c-.99.71-2.25,1.04-3.79,1.04s-2.92-.4-3.94-1.21c-1.02-.81-1.79-1.91-2.27-3.29-.5-1.37-.75-2.88-.75-4.51s.25-3.13.75-4.51c.48-1.37,1.24-2.47,2.27-3.3,1.02-.84,2.33-1.26,3.94-1.26s2.93.41,3.92,1.22c1,.81,1.69,1.81,2.04,2.95h8.09c-.87-3.49-2.53-6.14-4.99-7.94-2.46-1.8-5.46-2.68-9-2.68-2.98,0-5.57.67-7.79,2-2.2,1.34-3.89,3.17-5.1,5.52-1.2,2.34-1.8,5.02-1.8,8s.61,5.71,1.83,8.04c1.22,2.32,2.94,4.15,5.14,5.48,2.21,1.34,4.77,2.01,7.72,2.01,2.36,0,4.49-.43,6.41-1.25,1.91-.84,3.52-2.05,4.84-3.65,1.31-1.59,2.23-3.5,2.74-5.73h-8.09c-.47,1.35-1.19,2.38-2.18,3.07Z',
  'M152.14,52.7c0,2.28-.56,4.08-1.67,5.43-1.12,1.35-2.81,2.03-5.08,2.03s-3.85-.7-4.99-2.09c-1.13-1.39-1.7-3.19-1.7-5.38v-16.42h-7.58v17.56c0,2.46.47,4.67,1.43,6.6.95,1.93,2.32,3.45,4.11,4.54,1.79,1.09,3.96,1.64,6.51,1.64,1.75,0,3.44-.36,5.05-1.07,1.61-.72,2.92-1.75,3.91-3.1l1.25,3.47h6.39v-29.62s-7.63,0-7.63,0v16.41Z',
  'M174.86,43.29c1.03-.84,2.37-1.26,4-1.26s2.95.42,3.97,1.23c1.01.81,1.7,1.8,2.06,2.95h8.18c-.87-3.5-2.55-6.14-5.04-7.94-2.49-1.79-5.52-2.68-9.11-2.69-3.02,0-5.65.67-7.88,2-2.24,1.34-3.95,3.18-5.17,5.52-1.22,2.35-1.83,5.02-1.83,8.01s.62,5.7,1.85,8.03c1.23,2.32,2.97,4.16,5.2,5.49,2.23,1.33,4.84,2.01,7.82,2.01,2.38,0,4.55-.42,6.48-1.25,1.94-.83,3.57-2.06,4.9-3.65,1.33-1.59,2.27-3.5,2.78-5.73h-8.19c-.48,1.35-1.21,2.37-2.21,3.07-.99.71-2.27,1.05-3.83,1.05-1.63,0-2.96-.42-3.99-1.23-1.04-.81-1.81-1.91-2.31-3.28-.5-1.38-.75-2.88-.75-4.51s.26-3.14.76-4.51,1.26-2.47,2.3-3.31Z',
  'M208.36,29.91h-7.65v6.39h-5.55v6.39h5.55v16.84c0,3.53,2.85,6.39,6.38,6.39h9.26s0-6.38,0-6.38h-8v-16.84h8.01v-6.39h-8.01v-6.4Z',
  'M224.02,22.58c-1.35,0-2.52.48-3.5,1.43-.97.95-1.46,2.13-1.46,3.52s.49,2.52,1.46,3.5c.97.97,2.14,1.46,3.5,1.46,1.39,0,2.56-.48,3.52-1.46.96-.97,1.44-2.14,1.44-3.49s-.48-2.57-1.44-3.53c-.95-.95-2.13-1.43-3.52-1.43Z',
];
const WORDMARK_POLYS = [
  '264.28 36.32 276.14 64.56 269.71 79.07 277.67 79.07 283.55 65.31 280.27 56.5 272.71 36.32 264.28 36.32',
  '280.27 56.5 287.33 56.5 295.96 36.32 288 36.32 280.27 56.5',
  '102.68 24.09 93.23 49.91 90.98 56.06 87.39 65.89 95.5 65.89 98.92 56.07 109.76 56.07 112.04 49.92 101.08 49.92 108.07 29.88 120.63 65.9 128.75 65.9 113.48 24.09 102.68 24.09',
];

function iconFills(tone: BrandTone): { light: string; mid: string } {
  switch (tone) {
    case 'mono-white':
      return { light: '#ffffff', mid: '#ffffff' };
    case 'mono-navy':
      return { light: BRAND_NAVY, mid: BRAND_NAVY };
    default:
      return { light: BRAND_LIGHT, mid: BRAND_MID };
  }
}

/**
 * The interlocking "sync" mark on its own (no wordmark).
 * Height-driven; width is derived to preserve the 132.1×176.83 aspect ratio.
 */
export function BrandMark({
  size = 28,
  tone = 'color',
  title = 'AuctiSync',
  ...rest
}: {
  size?: number;
  tone?: BrandTone;
  title?: string;
} & Omit<React.SVGProps<SVGSVGElement>, 'ref'>): React.ReactElement {
  const f = iconFills(tone);
  const width = Math.round(size * (132.1 / 176.83) * 100) / 100;
  return (
    <svg
      {...rest}
      viewBox="0 0 132.1 176.83"
      width={width}
      height={size}
      role="img"
      aria-label={title}
    >
      {ICON_MARK_MID.map((p, i) => (
        <polygon key={`m${i}`} points={p} fill={f.mid} />
      ))}
      {ICON_MARK_LIGHT.map((p, i) => (
        <polygon key={`l${i}`} points={p} fill={f.light} />
      ))}
    </svg>
  );
}

/**
 * Full horizontal lockup: icon + "AuctiSync" wordmark.
 * Height-driven; width preserves the 359.2×89.32 aspect ratio.
 *
 * tone='color' (default): two-tone blue icon + wordmark in `wordmarkColor`
 *   (defaults to currentColor, so it adapts to the surrounding text colour).
 * tone='mono-white' / 'mono-navy': the whole lockup in one colour.
 */
export function Logo({
  height = 24,
  tone = 'color',
  wordmarkColor = 'currentColor',
  title = 'AuctiSync',
  ...rest
}: {
  height?: number;
  tone?: BrandTone;
  wordmarkColor?: string;
  title?: string;
} & Omit<React.SVGProps<SVGSVGElement>, 'ref'>): React.ReactElement {
  const f = iconFills(tone);
  const text =
    tone === 'mono-white' ? '#ffffff' : tone === 'mono-navy' ? BRAND_NAVY : wordmarkColor;
  const width = Math.round(height * (359.2 / 89.32) * 100) / 100;
  return (
    <svg
      {...rest}
      viewBox="0 0 359.2 89.32"
      width={width}
      height={height}
      role="img"
      aria-label={title}
    >
      {LOCKUP_ICON_MID.map((p, i) => (
        <polygon key={`im${i}`} points={p} fill={f.mid} />
      ))}
      {LOCKUP_ICON_LIGHT.map((p, i) => (
        <polygon key={`il${i}`} points={p} fill={f.light} />
      ))}
      {WORDMARK_PATHS.map((d, i) => (
        <path key={`wp${i}`} d={d} fill={text} />
      ))}
      {WORDMARK_POLYS.map((p, i) => (
        <polygon key={`wg${i}`} points={p} fill={text} />
      ))}
      <rect x="220.19" y="36.31" width="7.64" height="29.62" fill={text} />
    </svg>
  );
}
